import { useContext, useState } from "react";
import { AuthenticationContext } from "../../../contexts/authentication-context";
import { CompanyContext } from "../../../contexts/company-context";
import { Button, message, Modal, Space, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { DeleteFilled, PlusOutlined } from "@ant-design/icons";
import {
  EmployeeDetails,
  ResponseEmployeeBranchAccess,
} from "../../../serviceoperation/entity/EmployeeOperationEntity";
import roleOptions from "../../../Util/constants";
import AddAccess from "./AddAccess";
import { EmployeeOperation } from "../../../serviceoperation/EmployeeOperation";

interface Props {
  employeez: EmployeeDetails | null;
}

const BranchAccess = ({ employeez }: Props) => {
  const userInfo = useContext(AuthenticationContext)?.userInfo;
  const { selectedCompany } = useContext(CompanyContext);

  const [empData, setEmpData] = useState<EmployeeDetails | null>(employeez);

  const [openAddAccess, setOpenAddAccess] = useState(false);
  const [refreshReasons, setRefreshReasons] = useState<boolean>(false);

  const onAccessCreate = (values: any) => {
    setOpenAddAccess(false);

    // Append the new access to the empData state.
    const newAccess: ResponseEmployeeBranchAccess = {
      accessId: values.accessId,
      branchId: values.branchId,
      branchName:
        empData?.organization?.branchName || "Branch " + values.branchId,
      roleId: values.roleId,
      createdDateTimeUTC: new Date().toISOString(),
    };

    setEmpData((prevData) => {
      if (!prevData) return prevData;

      // Check to make sure a previous record with same AccessId doesn't exist
      // if it does then replace the old record with the new.
      const existingAccessIndex = prevData.access.branchAccess?.findIndex(
        (access) => access.accessId === newAccess.accessId
      );
      if (existingAccessIndex !== undefined && existingAccessIndex >= 0) {
        const updatedBranchAccess = [...(prevData.access.branchAccess || [])];
        updatedBranchAccess[existingAccessIndex] = newAccess;
        return {
          ...prevData,
          access: {
            ...prevData.access,
            branchAccess: updatedBranchAccess,
          },
        };
      }

      return {
        ...prevData,
        access: {
          ...prevData.access,
          branchAccess: [...(prevData.access.branchAccess || []), newAccess],
        },
      };
    });

    //Show Alert successfully inserted schedule???
    message.success("Successfully created access!");
  };

  const { confirm } = Modal;
  const deleteEmployeeBranchAccess = async (accessId: number) => {
    if (!empData) return;
    confirm({
      title: `Are you sure you want to terminate access?`,
      async onOk() {
        try {
          const employeeOps = new EmployeeOperation();
          const result = await employeeOps.DeleteEmployeeBranchAccessAsync(
            selectedCompany?.corporationId as number,
            empData.employeeId,
            accessId
          );
          if (result) {
            message.success("Successfully terminated employee.");
            // Remove the Accessfrom the empData state.
            const updatedAccess =
              empData.access.branchAccess?.filter(
                (access) => access.accessId !== accessId
              ) || [];
            setEmpData({
              ...empData,
              access: {
                ...empData.access,
                branchAccess: updatedAccess,
              },
            });
            return;
          } else {
            message.error("An error occured while processing your request.");
          }
        } catch (e) {
          message.error("An error occured while processing your request.");
        }
      },
      onCancel() {
        return;
      },
    });
  };

  const columns: ColumnsType<ResponseEmployeeBranchAccess> = [
    {
      title: "AccessId",
      dataIndex: "accessId",
      width: "10%",
    },
    {
      title: "Location",
      dataIndex: "branchName",
      width: "30%",
      responsive: ["lg"],
    },
    {
      title: "Role",
      dataIndex: "roleId",
      width: "30%",
      responsive: ["lg"],
      render: (roleId: number) => {
        const role = roleOptions.find((r) => r.value === roleId);
        return role ? role.label : "Unknown";
      },
    },
    {
      title: "Created Date",
      dataIndex: "createdDateTimeUTC",
      width: "25%",
      responsive: ["lg"],
      render: (createdDateTime) => {
        // Convert createdDateTime to a Date object
        const date = new Date(createdDateTime);

        // Get the month abbreviation
        const monthAbbreviation = date.toLocaleString("default", {
          month: "short",
        });

        // Format the date as "Mon DD, YYYY HH:mm:ss"
        const formattedDate = `${monthAbbreviation} ${date.getDate()}, ${date.getFullYear()} ${date
          .getHours()
          .toString()
          .padStart(2, "0")}:${date
          .getMinutes()
          .toString()
          .padStart(2, "0")}:${date.getSeconds().toString().padStart(2, "0")}`;
        return formattedDate;
      },
    },
    {
      title: "Action",
      render: (_, record) => (
        <>
          <Space size="middle">
            {userInfo?.employeeCorporation?.find(
              (y) => y.corporationId === selectedCompany!.corporationId
            )?.employeeId !== empData?.employeeId ? (
              <DeleteFilled
                style={{ color: "red" }}
                onClick={() => {
                  deleteEmployeeBranchAccess(record.accessId);
                }}
              />
            ) : null}
          </Space>
        </>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setOpenAddAccess(true)}
        >
          Add Access
        </Button>
      </div>
      <Table
        columns={columns}
        pagination={false}
        dataSource={empData?.access.branchAccess || []}
      />
      <AddAccess
        open={openAddAccess}
        onCreate={onAccessCreate}
        onCancel={() => {
          setOpenAddAccess(false);
        }}
        employee={empData}
      />
    </>
  );
};

export default BranchAccess;
